import React, { useState, useEffect } from "react";
import { useUser } from "../provider/UserProvider";
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Chip,
  InputAdornment,
  Card,
  CardContent,
  FormControlLabel,
  Switch,
  CircularProgress,
  Pagination,
  Divider,
  Stack,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Alert,
  Snackbar,
} from "@mui/material";
import {
  Add as AddIcon,
  Search as SearchIcon,
  AttachMoney as MoneyIcon,
  Star as PointsIcon,
  CheckCircle as ActiveIcon,
  Cancel as InactiveIcon,
  Check as CheckIcon,
  Payment as PaymentIcon,
} from "@mui/icons-material";
import axiosInstance from "../utlis/axiosInstance";

// Razorpay Configuration (same as app version)
const RAZORPAY_CONFIG = {
  // Test keys (for development)
  TEST: {
    KEY_ID: "rzp_test_8pdj4LuDcGq5nF",
    KEY_SECRET: "16hZV9nJ8B3Wf9sH9R3TX5kC",
  },

  // Live keys (for production)
  LIVE: {
    KEY_ID: "rzp_test_8pdj4LuDcGq5nF",
    KEY_SECRET: "16hZV9nJ8B3Wf9sH9R3TX5kC",
  },

  // Current environment
  ENVIRONMENT: "TEST", // Change to 'LIVE' for production

  // Get current keys based on environment
  getCurrentKeys() {
    return this[this.ENVIRONMENT as keyof typeof RAZORPAY_CONFIG] as {
      KEY_ID: string;
      KEY_SECRET: string;
    };
  },

  // Get current key ID
  getKeyId() {
    return this.getCurrentKeys().KEY_ID;
  },

  // Get current key secret
  getKeySecret() {
    return this.getCurrentKeys().KEY_SECRET;
  },
};

// Payment configuration
const PAYMENT_CONFIG = {
  CURRENCY: "INR",
  COMPANY_NAME: "Acumen GPS",
  THEME_COLOR: "#3b82f6",
  SUPPORT_EMAIL: "support@acumengps.com",
  SUPPORT_PHONE: "+91-XXXXXXXXXX",
};

// Razorpay types
declare global {
  interface Window {
    Razorpay: any;
  }
}

interface RazorpayResponse {
  razorpay_payment_id: string;
  razorpay_order_id: string;
  razorpay_signature: string;
}

interface RazorpayOptions {
  key: string;
  amount: number;
  currency: string;
  name: string;
  description: string;
  order_id: string;
  handler: (response: RazorpayResponse) => void;
  prefill: {
    name: string;
    email: string;
    contact: string;
  };
  theme: {
    color: string;
  };
  modal: {
    ondismiss: () => void;
  };
}

interface PricingScheme {
  id: number;
  name: string;
  description: string;
  amount: number;
  number_of_points: number;
  is_active: boolean;
  created_at?: string;
  updated_at?: string;
  duration_days?: number | null;
  plan_type?: string | null;
  airtel_plan_code?: string | null;
}

interface PricingFormData {
  name: string;
  description: string;
  amount: number;
  number_of_points: number;
  is_active: boolean;
}

const PricingManagement: React.FC = () => {
  const { permissions: userPermissions, user } = useUser();

  // Check if user is admin
  const isAdmin = user?.user?.administrator;

  const [pricingSchemes, setPricingSchemes] = useState<PricingScheme[]>([]);
  const [filteredSchemes, setFilteredSchemes] = useState<PricingScheme[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Dialog states
  const [openAddDialog, setOpenAddDialog] = useState(false);

  // Payment states
  const [paymentLoading, setPaymentLoading] = useState<number | null>(null);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [paymentError, setPaymentError] = useState<string | null>(null);

  // SIM plan alert state
  const [showSimAlert, setShowSimAlert] = useState(false);

  // Initialize form data state
  const [formData, setFormData] = useState<PricingFormData>({
    name: "",
    description: "",
    amount: 0,
    number_of_points: 0,
    is_active: true,
  });

  // Separate plans by type
  const normalPlans = pricingSchemes.filter(plan => !plan.plan_type || plan.plan_type === 'normal');
  const simPlans = pricingSchemes.filter(plan => plan.plan_type === 'sim');

  // Load Razorpay script
  useEffect(() => {
    const loadRazorpayScript = () => {
      return new Promise((resolve) => {
        // Check if Razorpay is already loaded
        if (window.Razorpay) {
          resolve(true);
          return;
        }

        const script = document.createElement("script");
        script.src = "https://checkout.razorpay.com/v1/checkout.js";
        script.async = true;

        script.onload = () => {
          console.log("Razorpay script loaded successfully");
          resolve(true);
        };

        script.onerror = () => {
          console.error("Failed to load Razorpay script");
          resolve(false);
        };

        document.body.appendChild(script);
      });
    };

    loadRazorpayScript();
  }, []);

  const fetchPlans = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axiosInstance.get("/gpstracker/planhandler/");
      console.log("Fetched plans response:", response.data);

      if (
        response.data &&
        response.data.responseData &&
        Array.isArray(response.data.responseData)
      ) {
        setPricingSchemes(response.data.responseData);
        setFilteredSchemes(response.data.responseData);
      } else if (Array.isArray(response.data)) {
        setPricingSchemes(response.data);
        setFilteredSchemes(response.data);
      } else {
        console.warn("Unexpected response format:", response.data);
        setPricingSchemes([]);
        setFilteredSchemes([]);
      }
    } catch (error) {
      console.error("Error fetching plans:", error);
      setError("Failed to fetch plans. Please check your connection.");
      setPricingSchemes([]);
      setFilteredSchemes([]);
    } finally {
      setLoading(false);
    }
  };

  const createPlan = async (planData: PricingFormData) => {
    try {
      setLoading(true);
      const response = await axiosInstance.post(
        "/gpstracker/planhandler/",
        planData
      );
      console.log("Plan created:", response.data);

      // Refresh the plans list after creation
      await fetchPlans();

      return response.data;
    } catch (error) {
      console.error("Error creating plan:", error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Load plans on component mount
  useEffect(() => {
    fetchPlans();
  }, []);

  // Filter plans based on search query
  useEffect(() => {
    const filtered = pricingSchemes.filter(
      (scheme) =>
        scheme.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        scheme.description.toLowerCase().includes(searchQuery.toLowerCase())
    );
    setFilteredSchemes(filtered);
  }, [searchQuery, pricingSchemes]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  const handleTextInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (name === "amount" || name === "number_of_points") {
      setFormData((prev) => ({
        ...prev,
        [name]: parseFloat(value) || 0,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleSwitchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: checked,
    }));
  };

  const handleAddScheme = () => {
    setFormData({
      name: "",
      description: "",
      amount: 0,
      number_of_points: 0,
      is_active: true,
    });
    setOpenAddDialog(true);
  };

  const handleDialogClose = () => {
    setOpenAddDialog(false);
    setError(null);
  };

  const handleSaveScheme = async () => {
    try {
      setLoading(true);
      setError(null);

      // Create new plan
      await createPlan(formData);

      handleDialogClose();
    } catch (error: any) {
      setError(error.message || "Failed to save plan. Please try again.");
      console.error("Save error:", error);
    } finally {
      setLoading(false);
    }
  };

  // Mock features for display (since API doesn't provide features)
  const getFeatures = (scheme: PricingScheme) => {
    if (scheme.plan_type === 'sim') {
      return [
        "Airtel SIM card included",
        "Pre-loaded balance",
        "Network coverage across India",
        "24/7 customer support",
        scheme.airtel_plan_code ? `Plan Code: ${scheme.airtel_plan_code}` : "Standard SIM plan"
      ];
    }
    
    return [
      "Access to essential tools",
      "Basic reporting and analytics",
      "Single-user access with permissions",
      `${scheme.number_of_points} points included`,
    ];
  };

  // Get plan type label
  const getPlanTypeLabel = (scheme: PricingScheme) => {
    if (scheme.plan_type === 'sim') {
      return 'SIM Plan';
    }
    return 'Normal Plan';
  };

  // Get plan type color
  const getPlanTypeColor = (scheme: PricingScheme) => {
    if (scheme.plan_type === 'sim') {
      return '#10b981'; // Green for SIM plans
    }
    return '#667eea'; // Blue for normal plans
  };

  // Payment Service (similar to app version)
  const PaymentService = {
    createOrder: async (userId: string, planId: string) => {
      return new Promise((resolve) => {
        setTimeout(() => {
          const orderId = `order_${Date.now()}_${Math.random()
            .toString(36)
            .substring(2, 15)}`;

          // Find the plan to get amount
          const plan = pricingSchemes.find((p) => p.id.toString() === planId);
          if (!plan) {
            resolve({
              success: false,
              error: "Plan not found",
            });
            return;
          }

          resolve({
            success: true,
            orderId: {
              amount: plan.amount * 100, // Convert to paise
              currency: PAYMENT_CONFIG.CURRENCY,
              description: `Payment for ${plan.name}`,
            },
          });
        }, 500); // Simulate API delay
      });
    },

    initiatePayment: async (paymentData: any) => {
      return new Promise((resolve, reject) => {
        try {
          // Check if Razorpay is loaded
          if (!window.Razorpay) {
            reject(new Error("Razorpay is not loaded"));
            return;
          }

          const options: any = {
            key: RAZORPAY_CONFIG.getKeyId(),
            amount: paymentData.amount,
            currency: paymentData.currency,
            name: paymentData.name,
            description: paymentData.description,
            handler: async (response: any) => {
              console.log("Payment successful:", response);
              resolve({
                success: true,
                paymentId: response.razorpay_payment_id,
                orderId: response.razorpay_order_id || "demo_order",
                signature: response.razorpay_signature || "demo_signature",
              });
            },
            prefill: paymentData.prefill,
            theme: {
              color: PAYMENT_CONFIG.THEME_COLOR,
            },
            modal: {
              ondismiss: () => {
                console.log("Payment modal closed");
                reject(new Error("Payment cancelled by user"));
              },
            },
          };

          const razorpay = new window.Razorpay(options);
          razorpay.open();
        } catch (error) {
          reject(error);
        }
      });
    },
  };

  // Payment handler (same logic as app version)
  const handlePayment = async (scheme: PricingScheme) => {
    try {
      setPaymentLoading(scheme.id);
      setPaymentError(null);

      // Create order using PaymentService (same as app)
      const paymentResult = (await PaymentService.createOrder(
        "demo_user_id", // Demo user ID
        scheme.id.toString()
      )) as any;

      if (paymentResult.success) {
        let body = {
          amount: paymentResult.orderId?.amount,
          currency: paymentResult.orderId?.currency,
          name: PAYMENT_CONFIG.COMPANY_NAME,
          description: paymentResult.orderId?.description,
          prefill: {
            email: "demo@example.com",
            contact: "9999999999",
            name: "Demo User",
          },
        };

        const paymentInitiationResult = await PaymentService.initiatePayment(
          body
        );
        console.log("Payment result:", paymentInitiationResult);

        // Payment successful
        setPaymentSuccess(true);
      } else {
        setPaymentError(
          paymentResult.error || "Payment failed. Please try again."
        );
      }
    } catch (error: any) {
      console.error("Payment Error:", error);
      if (error.message === "Payment cancelled by user") {
        // User closed the payment modal
        console.log("Payment was cancelled by user");
      } else {
        setPaymentError(
          "Payment failed. Please check your connection and try again."
        );
      }
    } finally {
      setPaymentLoading(null);
    }
  };

  // SIM plan click handler
  const handleSimPlanClick = (scheme: PricingScheme) => {
    setShowSimAlert(true);
  };

  // Close snackbars
  const handleClosePaymentSuccess = () => {
    setPaymentSuccess(false);
  };

  const handleClosePaymentError = () => {
    setPaymentError(null);
  };

  // Close SIM alert
  const handleCloseSimAlert = () => {
    setShowSimAlert(false);
  };

  return (
    <Box sx={{ backgroundColor: "#f8fafc", minHeight: "100vh" }}>
      <Container maxWidth="xl" sx={{ py: 6, px: { xs: 4, sm: 6, md: 8 } }}>
        {/* Search and Actions Bar */}
        <Paper
          elevation={0}
          sx={{
            p: 3,
            mb: 4,
            border: "1px solid #e2e8f0",
            borderRadius: 3,
            backgroundColor: "#ffffff",
            boxShadow: "0 2px 4px rgba(0, 0, 0, 0.05)",
          }}
        >
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            alignItems={{ xs: "stretch", sm: "center" }}
          >
            <TextField
              placeholder="Search plans by name or description..."
              variant="outlined"
              size="small"
              value={searchQuery}
              onChange={handleSearchChange}
              sx={{
                flexGrow: 1,
                "& .MuiOutlinedInput-root": {
                  backgroundColor: "#f8fafc",
                  borderRadius: 2,
                  "& fieldset": {
                    borderColor: "#e2e8f0",
                  },
                  "&:hover fieldset": {
                    borderColor: "#cbd5e1",
                  },
                  "&.Mui-focused fieldset": {
                    borderColor: "#667eea",
                    borderWidth: 2,
                  },
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: "#64748b", fontSize: 20 }} />
                  </InputAdornment>
                ),
              }}
            />
            {isAdmin && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleAddScheme}
                disabled={loading}
                sx={{
                  px: 3,
                  py: 1,
                  bgcolor: "var(--primary-color)",
                  borderRadius: 2,
                  boxShadow: "0 2px 4px rgba(29, 43, 86, 0.2)",
                  "&:hover": {
                    bgcolor: "var(--primary-light)",
                    boxShadow: "0 4px 8px rgba(29, 43, 86, 0.3)",
                  },
                  "&:disabled": {
                    bgcolor: "#9ca3af",
                  },
                  textTransform: "none",
                  fontWeight: 600,
                  minWidth: "140px",
                  fontSize: "0.875rem",
                }}
              >
                Add Plan
              </Button>
            )}
          </Stack>
        </Paper>

        {/* Error Alert */}
        {error && (
          <Paper
            elevation={0}
            sx={{
              p: 3,
              mb: 4,
              backgroundColor: "#fef2f2",
              border: "1px solid #fecaca",
              borderRadius: 3,
            }}
          >
            <Typography sx={{ color: "#dc2626", fontWeight: 500 }}>
              {error}
            </Typography>
          </Paper>
        )}

        {/* Loading State */}
        {loading && filteredSchemes.length === 0 && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
            <CircularProgress size={48} sx={{ color: "#667eea" }} />
          </Box>
        )}

        {/* Empty State */}
        {!loading && filteredSchemes.length === 0 && (
          <Paper
            elevation={0}
            sx={{
              p: 8,
              textAlign: "center",
              backgroundColor: "#ffffff",
              border: "1px solid #e2e8f0",
              borderRadius: 4,
              boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
            }}
          >
            <MoneyIcon sx={{ fontSize: 64, color: "#94a3b8", mb: 3 }} />
            <Typography
              variant="h5"
              sx={{ color: "#1e293b", mb: 2, fontWeight: 600 }}
            >
              {searchQuery ? "No plans match your search" : "No plans found"}
            </Typography>
            <Typography
              variant="body1"
              sx={{ color: "#64748b", mb: 4, maxWidth: "400px", mx: "auto" }}
            >
              {searchQuery
                ? "Try adjusting your search terms or create a new plan"
                : isAdmin
                  ? "Get started by creating your first subscription plan"
                  : "No subscription plans are currently available. Please contact your administrator."}
            </Typography>
            {!searchQuery && isAdmin && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleAddScheme}
                sx={{
                  background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                  "&:hover": {
                    background: "linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%)"
                  },
                  textTransform: "none",
                  fontWeight: 600,
                  px: 4,
                  py: 1.5,
                  borderRadius: 2,
                }}
              >
                Create First Plan
              </Button>
            )}
          </Paper>
        )}

        {/* Normal Plans Section */}
        {normalPlans.length > 0 && (
          <Box sx={{ mb: 6 }}>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                color: "#1e293b",
                mb: 3,
                textAlign: "center",
                position: "relative",
                "&::after": {
                  content: '""',
                  position: "absolute",
                  bottom: -8,
                  left: "50%",
                  transform: "translateX(-50%)",
                  width: 60,
                  height: 4,
                  backgroundColor: "#667eea",
                  borderRadius: 2,
                },
              }}
            >
              GPS Tracking Plans
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: "#64748b",
                mb: 4,
                textAlign: "center",
                maxWidth: "600px",
                mx: "auto",
              }}
            >
              Choose from our range of GPS tracking plans with different point allocations
            </Typography>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  md: "repeat(2, 1fr)",
                  lg: "repeat(3, 1fr)",
                },
                gap: 3,
                mb: 4,
                maxWidth: "1000px",
                mx: "auto",
              }}
            >
              {normalPlans.map((scheme, index) => (
                <Card
                  key={scheme.id}
                  elevation={0}
                  sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    backgroundColor: "#ffffff",
                    border: scheme.is_active
                      ? "2px solid #667eea"
                      : "1px solid #e2e8f0",
                    borderRadius: 6,
                    position: "relative",
                    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      transform: "translateY(-4px)",
                      boxShadow: "0 8px 25px rgba(0, 0, 0, 0.12)",
                      borderColor: "#667eea",
                    },
                    // Add gradient background based on index
                    background: index % 3 === 0
                      ? "linear-gradient(135deg, #ffffff 0%, #fef2f2 100%)"
                      : index % 3 === 1
                        ? "linear-gradient(135deg, #ffffff 0%, #fefce8 100%)"
                        : "linear-gradient(135deg, #ffffff 0%, #f0fdf4 100%)",
                  }}
                >
                  {/* Active Badge */}
                  {scheme.is_active && (
                    <Box
                      sx={{
                        position: "absolute",
                        top: 8,
                        right: 16,
                        background: "rgba(255, 255, 255, 0.95)",
                        color: "#1e293b",
                        px: 2,
                        py: 0.75,
                        borderRadius: 20,
                        fontSize: "0.75rem",
                        fontWeight: 700,
                        letterSpacing: "0.05em",
                        textTransform: "uppercase",
                        boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
                        border: "2px solid rgba(16, 185, 129, 0.3)",
                        backdropFilter: "blur(10px)",
                      }}
                    >
                      Active
                    </Box>
                  )}

                  {/* Plan Type Badge */}
                  <Box
                    sx={{
                      position: "absolute",
                      top: 8,
                      left: 16,
                      background: "rgba(255, 255, 255, 0.95)",
                      color: getPlanTypeColor(scheme),
                      px: 2,
                      py: 0.75,
                      borderRadius: 20,
                      fontSize: "0.75rem",
                      fontWeight: 700,
                      letterSpacing: "0.05em",
                      textTransform: "uppercase",
                      boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
                      border: `2px solid ${getPlanTypeColor(scheme)}40`,
                      backdropFilter: "blur(10px)",
                    }}
                  >
                    {getPlanTypeLabel(scheme)}
                  </Box>

                  <CardContent
                    sx={{
                      p: 3,
                      flexGrow: 1,
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    {/* Plan Name */}
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 700,
                        mb: 2,
                        color: "#1e293b",
                        fontSize: "1.25rem",
                      }}
                    >
                      {scheme.name}
                    </Typography>

                    {/* Price */}
                    <Box sx={{ mb: 2 }}>
                      <Stack direction="row" alignItems="baseline" spacing={1}>
                        <Typography
                          variant="h4"
                          sx={{
                            fontWeight: 800,
                            color: "#1e293b",
                            fontSize: "2rem",
                          }}
                        >
                          ₹{scheme.amount.toLocaleString()}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: "#64748b",
                            fontWeight: 500,
                          }}
                        >
                          per plan
                        </Typography>
                      </Stack>
                    </Box>

                    {/* Description */}
                    <Typography
                      variant="body2"
                      sx={{
                        color: "#64748b",
                        mb: 3,
                        lineHeight: 1.5,
                        flexGrow: 1,
                      }}
                    >
                      {scheme.description}
                    </Typography>

                    {/* CTA Button */}
                    <Button
                      variant={scheme.is_active ? "contained" : "outlined"}
                      fullWidth
                      onClick={() =>
                        scheme.is_active ? handlePayment(scheme) : undefined
                      }
                      disabled={paymentLoading === scheme.id}
                      startIcon={
                        paymentLoading === scheme.id ? (
                          <CircularProgress size={16} color="inherit" />
                        ) : scheme.is_active ? (
                          <PaymentIcon />
                        ) : undefined
                      }
                      sx={{
                        py: 1,
                        mb: 3,
                        borderRadius: 3,
                        textTransform: "none",
                        fontWeight: 600,
                        fontSize: "0.875rem",
                        ...(scheme.is_active
                          ? {
                            backgroundColor: "#667eea",
                            "&:hover": {
                              backgroundColor: "#5a67d8",
                            },
                            "&:disabled": {
                              backgroundColor: "#9ca3af",
                            },
                          }
                          : {
                            borderColor: "#e2e8f0",
                            color: "#374151",
                            "&:hover": {
                              borderColor: "#cbd5e1",
                              backgroundColor: "#f8fafc",
                            },
                          }),
                      }}
                    >
                      {paymentLoading === scheme.id
                        ? "Processing..."
                        : scheme.is_active
                          ? "Buy Now"
                          : "Start for Free"}
                    </Button>

                    {/* Features List */}
                    <List sx={{ p: 0 }}>
                      {getFeatures(scheme).map((feature, featureIndex) => (
                        <ListItem key={featureIndex} sx={{ px: 0, py: 0.25 }}>
                          <ListItemIcon sx={{ minWidth: 24 }}>
                            <CheckIcon
                              sx={{ color: "#10b981", fontSize: 16 }}
                            />
                          </ListItemIcon>
                          <ListItemText
                            primary={feature}
                            primaryTypographyProps={{
                              fontSize: "0.75rem",
                              color: "#475569",
                              fontWeight: 400,
                            }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </CardContent>
                </Card>
              ))}
            </Box>
          </Box>
        )}

        {/* SIM Plans Section */}
        {simPlans.length > 0 && (
          <Box sx={{ mb: 6 }}>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                color: "#1e293b",
                mb: 3,
                textAlign: "center",
                position: "relative",
                "&::after": {
                  content: '""',
                  position: "absolute",
                  bottom: -8,
                  left: "50%",
                  transform: "translateX(-50%)",
                  width: 60,
                  height: 4,
                  backgroundColor: "#10b981",
                  borderRadius: 2,
                },
              }}
            >
              SIM Card Plans
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: "#64748b",
                mb: 4,
                textAlign: "center",
                maxWidth: "600px",
                mx: "auto",
              }}
            >
              Get Airtel SIM cards with pre-loaded balance for your GPS tracking devices
            </Typography>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xs: "1fr",
                  md: "repeat(2, 1fr)",
                  lg: "repeat(3, 1fr)",
                },
                gap: 3,
                mb: 4,
                maxWidth: "1000px",
                mx: "auto",
              }}
            >
              {simPlans.map((scheme, index) => (
                <Card
                  key={scheme.id}
                  elevation={0}
                  sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    backgroundColor: "#ffffff",
                    border: scheme.is_active
                      ? "2px solid #10b981"
                      : "1px solid #e2e8f0",
                    borderRadius: 6,
                    position: "relative",
                    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      transform: "translateY(-4px)",
                      boxShadow: "0 8px 25px rgba(0, 0, 0, 0.12)",
                      borderColor: "#10b981",
                    },
                    // Add gradient background for SIM plans
                    background: "linear-gradient(135deg, #ffffff 0%, #f0fdf4 100%)",
                  }}
                >
                  {/* Active Badge */}
                  {scheme.is_active && (
                    <Box
                      sx={{
                        position: "absolute",
                        top: 8,
                        right: 16,
                        background: "rgba(255, 255, 255, 0.95)",
                        color: "#1e293b",
                        px: 2,
                        py: 0.75,
                        borderRadius: 20,
                        fontSize: "0.75rem",
                        fontWeight: 700,
                        letterSpacing: "0.05em",
                        textTransform: "uppercase",
                        boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
                        border: "2px solid rgba(16, 185, 129, 0.3)",
                        backdropFilter: "blur(10px)",
                      }}
                    >
                      Active
                    </Box>
                  )}

                  {/* Plan Type Badge */}
                  <Box
                    sx={{
                      position: "absolute",
                      top: 8,
                      left: 16,
                      background: "rgba(255, 255, 255, 0.95)",
                      color: "#10b981",
                      px: 2,
                      py: 0.75,
                      borderRadius: 20,
                      fontSize: "0.75rem",
                      fontWeight: 700,
                      letterSpacing: "0.05em",
                      textTransform: "uppercase",
                      boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
                      border: "2px solid rgba(16, 185, 129, 0.3)",
                      backdropFilter: "blur(10px)",
                    }}
                  >
                    SIM Plan
                  </Box>

                  <CardContent
                    sx={{
                      p: 3,
                      flexGrow: 1,
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    {/* Plan Name */}
                    <Typography
                      variant="h6"
                      sx={{
                        fontWeight: 700,
                        mb: 2,
                        color: "#1e293b",
                        fontSize: "1.25rem",
                      }}
                    >
                      {scheme.name}
                    </Typography>

                    {/* Price */}
                    <Box sx={{ mb: 2 }}>
                      <Stack direction="row" alignItems="baseline" spacing={1}>
                        <Typography
                          variant="h4"
                          sx={{
                            fontWeight: 800,
                            color: "#1e293b",
                            fontSize: "2rem",
                          }}
                        >
                          ₹{scheme.amount.toLocaleString()}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: "#64748b",
                            fontWeight: 500,
                          }}
                        >
                          SIM + Balance
                        </Typography>
                      </Stack>
                    </Box>

                    {/* Description */}
                    <Typography
                      variant="body2"
                      sx={{
                        color: "#64748b",
                        mb: 3,
                        lineHeight: 1.5,
                        flexGrow: 1,
                      }}
                    >
                      {scheme.description}
                    </Typography>

                    {/* CTA Button */}
                    <Button
                      variant={scheme.is_active ? "contained" : "outlined"}
                      fullWidth
                      onClick={() =>
                        scheme.is_active ? handleSimPlanClick(scheme) : undefined
                      }
                      disabled={paymentLoading === scheme.id}
                      startIcon={
                        paymentLoading === scheme.id ? (
                          <CircularProgress size={16} color="inherit" />
                        ) : scheme.is_active ? (
                          <PaymentIcon />
                        ) : undefined
                      }
                      sx={{
                        py: 1,
                        mb: 3,
                        borderRadius: 3,
                        textTransform: "none",
                        fontWeight: 600,
                        fontSize: "0.875rem",
                        ...(scheme.is_active
                          ? {
                            backgroundColor: "#10b981",
                            "&:hover": {
                              backgroundColor: "#059669",
                            },
                            "&:disabled": {
                              backgroundColor: "#9ca3af",
                            },
                          }
                          : {
                            borderColor: "#e2e8f0",
                            color: "#374151",
                            "&:hover": {
                              borderColor: "#cbd5e1",
                              backgroundColor: "#f8fafc",
                            },
                          }),
                      }}
                    >
                      {paymentLoading === scheme.id
                        ? "Processing..."
                        : scheme.is_active
                          ? "Buy SIM"
                          : "Coming Soon"}
                    </Button>

                    {/* Features List */}
                    <List sx={{ p: 0 }}>
                      {getFeatures(scheme).map((feature, featureIndex) => (
                        <ListItem key={featureIndex} sx={{ px: 0, py: 0.25 }}>
                          <ListItemIcon sx={{ minWidth: 24 }}>
                            <CheckIcon
                              sx={{ color: "#10b981", fontSize: 16 }}
                            />
                          </ListItemIcon>
                          <ListItemText
                            primary={feature}
                            primaryTypographyProps={{
                              fontSize: "0.75rem",
                              color: "#475569",
                              fontWeight: 400,
                            }}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </CardContent>
                </Card>
              ))}
            </Box>
          </Box>
        )}

        {/* Enhanced Add Plan Dialog */}
        <Dialog
          open={openAddDialog}
          onClose={handleDialogClose}
          maxWidth="md"
          fullWidth
          PaperProps={{
            elevation: 0,
            sx: {
              borderRadius: 3,
              overflow: "hidden",
              boxShadow: "0 20px 40px rgba(0, 0, 0, 0.1)",
            },
          }}
        >
          {/* Dialog Header */}
          <Box
            sx={{
              backgroundColor: "#667eea",
              color: "white",
              p: 3,
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              Create New Plan
            </Typography>
          </Box>

          <DialogContent sx={{ p: 3 }}>
            <Stack spacing={3}>
              {/* Plan Name Section */}
              <Box>
                <Typography
                  variant="h6"
                  sx={{ mb: 2, fontWeight: 600, color: "#1e293b" }}
                >
                  Plan Details
                </Typography>
                <Stack spacing={2}>
                  <TextField
                    name="name"
                    label="Plan Name"
                    value={formData.name}
                    onChange={handleTextInputChange}
                    fullWidth
                    required
                    placeholder="e.g., Basic Plan, Premium Plan"
                    variant="outlined"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        backgroundColor: "#f8fafc",
                        borderRadius: 1.5,
                        "& fieldset": {
                          borderColor: "#e2e8f0",
                        },
                        "&:hover fieldset": {
                          borderColor: "#cbd5e1",
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: "#667eea",
                          borderWidth: 2,
                        },
                      },
                      "& .MuiInputLabel-root.Mui-focused": {
                        color: "#667eea",
                      },
                    }}
                  />

                  <TextField
                    name="description"
                    label="Description"
                    value={formData.description}
                    onChange={handleTextInputChange}
                    fullWidth
                    multiline
                    rows={3}
                    required
                    placeholder="Describe what this plan includes and its benefits..."
                    variant="outlined"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        backgroundColor: "#f8fafc",
                        borderRadius: 1.5,
                        "& fieldset": {
                          borderColor: "#e2e8f0",
                        },
                        "&:hover fieldset": {
                          borderColor: "#cbd5e1",
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: "#667eea",
                          borderWidth: 2,
                        },
                      },
                      "& .MuiInputLabel-root.Mui-focused": {
                        color: "#667eea",
                      },
                    }}
                  />
                </Stack>
              </Box>

              <Divider />

              {/* Pricing Section */}
              <Box>
                <Typography
                  variant="h6"
                  sx={{ mb: 2, fontWeight: 600, color: "#1e293b" }}
                >
                  Pricing & Points
                </Typography>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                    gap: 2,
                  }}
                >
                  <TextField
                    name="amount"
                    label="Amount (₹)"
                    type="number"
                    value={formData.amount}
                    onChange={handleTextInputChange}
                    fullWidth
                    required
                    inputProps={{ min: 0, step: 0.01 }}
                    variant="outlined"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        backgroundColor: "#f8fafc",
                        borderRadius: 1.5,
                        "& fieldset": {
                          borderColor: "#e2e8f0",
                        },
                        "&:hover fieldset": {
                          borderColor: "#cbd5e1",
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: "#667eea",
                          borderWidth: 2,
                        },
                      },
                      "& .MuiInputLabel-root.Mui-focused": {
                        color: "#667eea",
                      },
                    }}
                  />
                  <TextField
                    name="number_of_points"
                    label="Number of Points"
                    type="number"
                    value={formData.number_of_points}
                    onChange={handleTextInputChange}
                    fullWidth
                    required
                    inputProps={{ min: 0, step: 1 }}
                    variant="outlined"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        backgroundColor: "#f8fafc",
                        borderRadius: 1.5,
                        "& fieldset": {
                          borderColor: "#e2e8f0",
                        },
                        "&:hover fieldset": {
                          borderColor: "#cbd5e1",
                        },
                        "&.Mui-focused fieldset": {
                          borderColor: "#667eea",
                          borderWidth: 2,
                        },
                      },
                      "& .MuiInputLabel-root.Mui-focused": {
                        color: "#667eea",
                      },
                    }}
                  />
                </Box>
              </Box>

              <Divider />

              {/* Settings Section */}
              <Box>
                <Typography
                  variant="h6"
                  sx={{ mb: 2, fontWeight: 600, color: "#1e293b" }}
                >
                  Plan Settings
                </Typography>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    backgroundColor: "#f8fafc",
                    border: "1px solid #e2e8f0",
                    borderRadius: 1.5,
                  }}
                >
                  <FormControlLabel
                    control={
                      <Switch
                        name="is_active"
                        checked={formData.is_active}
                        onChange={handleSwitchChange}
                        sx={{
                          "& .MuiSwitch-switchBase.Mui-checked": {
                            color: "#667eea",
                          },
                          "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                          {
                            backgroundColor: "#667eea",
                          },
                        }}
                      />
                    }
                    label={
                      <Box>
                        <Typography
                          variant="body1"
                          sx={{ fontWeight: 600, color: "#1e293b" }}
                        >
                          Active Plan
                        </Typography>
                        <Typography variant="body2" sx={{ color: "#64748b" }}>
                          Make this plan available for subscription immediately
                        </Typography>
                      </Box>
                    }
                  />
                </Paper>
              </Box>
            </Stack>
          </DialogContent>

          {/* Dialog Footer */}
          <Box
            sx={{
              p: 3,
              backgroundColor: "#f8fafc",
              borderTop: "1px solid #e2e8f0",
              display: "flex",
              gap: 2,
              justifyContent: "flex-end",
            }}
          >
            <Button
              onClick={handleDialogClose}
              variant="outlined"
              disabled={loading}
              sx={{
                borderColor: "#e2e8f0",
                color: "#374151",
                "&:hover": {
                  borderColor: "#cbd5e1",
                  backgroundColor: "#f8fafc",
                },
                textTransform: "none",
                fontWeight: 500,
                px: 3,
                py: 1,
                borderRadius: 1.5,
                fontSize: "0.875rem",
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSaveScheme}
              variant="contained"
              disabled={!formData.name || !formData.description || loading}
              sx={{
                bgcolor: "var(--primary-color)",
                "&:hover": {
                  bgcolor: "var(--primary-light)",
                },
                "&:disabled": {
                  bgcolor: "#9ca3af",
                },
                textTransform: "none",
                fontWeight: 600,
                px: 4,
                py: 1,
                borderRadius: 1.5,
                boxShadow: "0 2px 4px rgba(29, 43, 86, 0.2)",
                fontSize: "0.875rem",
              }}
            >
              {loading ? "Creating..." : "Create Plan"}
            </Button>
          </Box>
        </Dialog>

        {/* Payment Success Snackbar */}
        <Snackbar
          open={paymentSuccess}
          autoHideDuration={6000}
          onClose={handleClosePaymentSuccess}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <Alert
            onClose={handleClosePaymentSuccess}
            severity="success"
            variant="filled"
            sx={{ width: "100%" }}
          >
            Payment successful! Your plan has been activated.
          </Alert>
        </Snackbar>

        {/* Payment Error Snackbar */}
        <Snackbar
          open={!!paymentError}
          autoHideDuration={6000}
          onClose={handleClosePaymentError}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <Alert
            onClose={handleClosePaymentError}
            severity="error"
            variant="filled"
            sx={{ width: "100%" }}
          >
            {paymentError}
          </Alert>
        </Snackbar>

        {/* SIM Alert Snackbar */}
        <Snackbar
          open={showSimAlert}
          autoHideDuration={6000}
          onClose={handleCloseSimAlert}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <Alert
            onClose={handleCloseSimAlert}
            severity="info"
            variant="filled"
            sx={{ width: "100%" }}
          >
            For SIM card plans, please download our mobile app to activate your SIM.
          </Alert>
        </Snackbar>
      </Container>
      <style>
        {`
            @keyframes float {
              0%, 100% { transform: translateY(0px); }
              50% { transform: translateY(-10px); }
            }
            @keyframes pulse {
              0%, 100% { transform: scale(1); }
              50% { transform: scale(1.05); }
            }
          `}
      </style>
    </Box>
  );
};

export default PricingManagement;